import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { FaReact, FaApple, FaAndroid, FaCode, FaMobileAlt, FaMapMarkedAlt, FaServer, FaEnvelope, FaLinkedin, FaGithub } from "react-icons/fa";
import { SiTypescript, SiRedux, SiSocketdotio } from "react-icons/si";

const Typewriter = ({ words }) => {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [reverse, setReverse] = useState(false);
  const [blink, setBlink] = useState(true);

  useEffect(() => {
    if (index === words.length) return;

    if (subIndex === words[index].length + 1 && !reverse) {
      setReverse(true);
      return;
    }

    if (subIndex === 0 && reverse) {
      setReverse(false);
      setIndex((prev) => (prev + 1) % words.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => prev + (reverse ? -1 : 1));
    }, Math.max(reverse ? 75 : subIndex === words[index].length ? 1000 : 150, parseInt(Math.random() * 350)));

    return () => clearTimeout(timeout);
  }, [subIndex, index, reverse, words]);

  useEffect(() => {
    const timeout2 = setTimeout(() => {
      setBlink((prev) => !prev);
    }, 500);
    return () => clearTimeout(timeout2);
  }, [blink]);

  return (
    <span className="text-accent">
      {`${words[index].substring(0, subIndex)}${blink ? "|" : " "}`}
    </span>
  );
};

const SkillCard = ({ icon: Icon, name, level }) => (
  <motion.div
    whileHover={{ y: -8, scale: 1.05 }}
    className="glass-card flex flex-col items-center gap-4 p-6 min-w-[150px] group cursor-default"
  >
    <div className="p-4 rounded-full bg-gradient-to-br from-accent/20 to-neon-purple/20 text-4xl text-accent group-hover:scale-110 transition-transform duration-300">
      <Icon />
    </div>
    <h3 className="font-bold text-lg">{name}</h3>
    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${level}%` }}
        transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
        className="h-full bg-gradient-to-r from-accent via-neon-purple to-neon-pink"
      />
    </div>
  </motion.div>
);

const StatsCard = ({ number, label }) => (
  <motion.div
    whileHover={{ y: -5 }}
    className="text-center p-6"
  >
    <motion.h3
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-accent to-neon-purple bg-clip-text text-transparent mb-2"
    >
      {number}
    </motion.h3>
    <p className="text-slate-400 text-sm">{label}</p>
  </motion.div>
);

export default function Home() {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const y2 = useTransform(scrollY, [0, 500], [0, -150]);

  return (
    <div className="min-h-screen pt-20 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            style={{ y: y1, x: -100 }}
            className="absolute top-20 left-10 w-72 h-72 bg-neon-purple/20 rounded-full blur-[100px]"
          />
          <motion.div
            style={{ y: y2, x: 100 }}
            className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-[100px]"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-block px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
            >
              <span className="text-accent font-semibold">👋 Hello, I'm Sayed Hasan Ali Shah</span>
            </motion.div>

            <h1 className="text-4xl md:text-7xl font-bold leading-tight mb-6">
              Building <br />
              <Typewriter words={["Mobile Apps", "Digital Experiences", "Future Tech"]} />
            </h1>

            <p className="text-slate-400 text-lg mb-4 max-w-lg leading-relaxed">
              React Native Developer with 2+ years of experience building production-ready mobile applications for Android and iOS.
            </p>
            <p className="text-slate-400 text-lg mb-8 max-w-lg leading-relaxed">
              Skilled in JavaScript, TypeScript, Redux, and Google Maps API, with a strong focus on performance optimization, scalable architecture, and clean UI/UX design.
            </p>

            <div className="flex gap-4 mb-8">
              <Link to="/projects" className="btn-primary">
                View My Work
              </Link>
              <Link to="/contact" className="btn-outline">
                Contact Me
              </Link>
            </div>

            <div className="flex gap-4">
              <motion.a
                href="https://www.linkedin.com/in/sayed-hasan-ali-shah"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -3, color: "#38bdf8" }}
                className="text-slate-400 text-xl transition-colors"
              >
                <FaLinkedin />
              </motion.a>
              <motion.a
                href="https://github.com/SayedHasanAliShah"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -3, color: "#38bdf8" }}
                className="text-slate-400 text-xl transition-colors"
              >
                <FaGithub />
              </motion.a>
              <motion.a
                href="mailto:alihasanshah755@gmail.com"
                whileHover={{ y: -3, color: "#38bdf8" }}
                className="text-slate-400 text-xl transition-colors"
              >
                <FaEnvelope />
              </motion.a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative hidden md:block"
          >
            <div className="relative w-[300px] h-[600px] mx-auto mobile-frame bg-black">
              <div className="mobile-notch" />
              <div className="h-full w-full bg-slate-900 overflow-hidden relative">
                <div className="p-6 pt-12 space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="w-8 h-8 rounded-full bg-slate-700" />
                    <div className="w-4 h-4 rounded-full bg-slate-700" />
                  </div>
                  <div className="h-32 rounded-2xl bg-gradient-to-br from-accent to-neon-purple opacity-80" />
                  <div className="space-y-2">
                    <div className="h-4 w-3/4 rounded bg-slate-700" />
                    <div className="h-4 w-1/2 rounded bg-slate-700" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-8">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className="h-24 rounded-xl bg-slate-800 border border-slate-700" />
                    ))}
                  </div>
                </div>

                <motion.div
                  animate={{ y: [-10, 10, -10] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-20 -right-12 p-4 glass rounded-2xl text-4xl text-[#61DAFB]"
                >
                  <FaReact />
                </motion.div>
                <motion.div
                  animate={{ y: [10, -10, 10] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute bottom-40 -left-12 p-4 glass rounded-2xl text-4xl text-white"
                >
                  <FaApple />
                </motion.div>
                <motion.div
                  animate={{ y: [-15, 15, -15] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute top-1/2 -right-16 p-4 glass rounded-2xl text-4xl text-[#3DDC84]"
                >
                  <FaAndroid />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="glass-card rounded-3xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatsCard number="2+" label="Years Experience" />
              <StatsCard number="8+" label="Projects Delivered" />
              <StatsCard number="3+" label="Published Apps" />
              <StatsCard number="100%" label="Commitment" />
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 relative z-10">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Technical <span className="text-accent">Arsenal</span></h2>
            <p className="text-slate-400">Tools and technologies I use to bring ideas to life</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <SkillCard icon={FaReact} name="React Native" level={95} />
            <SkillCard icon={SiTypescript} name="TypeScript" level={90} />
            <SkillCard icon={FaCode} name="JavaScript" level={90} />
            <SkillCard icon={SiRedux} name="Redux" level={88} />
            <SkillCard icon={FaMobileAlt} name="iOS & Android" level={85} />
            <SkillCard icon={FaMapMarkedAlt} name="Maps API" level={85} />
            <SkillCard icon={FaServer} name="REST APIs" level={90} />
            <SkillCard icon={SiSocketdotio} name="Socket.io" level={80} />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 relative z-10">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold mb-6">
                About <span className="text-accent">Me</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                I'm a passionate React Native Developer with 2+ years of experience building and delivering production-ready mobile applications for Android and iOS.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                Currently working at Olumi Tech (Nov 2025 - Apr 2026), building scalable and user-friendly applications. Previously at Senew Tech (Jan 2025 - Nov 2025), where I developed multiple fitness, business, and streaming apps.
              </p>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                I hold a Bachelor of Software Engineering from Lahore Garrison University (2021-2025). I'm experienced in developing user-centric solutions and collaborating in Agile environments.
              </p>

              <div className="flex gap-4">
                <a
                  href="https://drive.google.com/drive/folders/1zTAADMmlP4ENvGhp_YX0KRjceIiyWKS9?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  Experience Letter - Olumi Tech
                </a>
                <a
                  href="https://drive.google.com/drive/folders/1v7NUnb_X6vzTitTUWYRVEXpXs_fP-4yM?usp=drive_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline inline-flex items-center gap-2"
                >
                  Experience Letter - Senew Tech
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="glass-card">
                <h3 className="font-bold text-xl mb-4 text-accent">Education</h3>
                <p className="text-white font-semibold">Bachelor of Software Engineering</p>
                <p className="text-slate-400">Lahore Garrison University</p>
                <p className="text-slate-500 text-sm">2021 - 2025</p>
              </div>

              <div className="glass-card">
                <h3 className="font-bold text-xl mb-4 text-accent">Languages</h3>
                <div className="flex gap-3">
                  <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-white/5 border border-white/10 text-slate-300">English</span>
                  <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-white/5 border border-white/10 text-slate-300">Urdu</span>
                  <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-white/5 border border-white/10 text-slate-300">Pashto</span>
                </div>
              </div>

              <div className="glass-card">
                <h3 className="font-bold text-xl mb-4 text-accent">Soft Skills</h3>
                <div className="flex flex-wrap gap-2">
                  {["Agile/Scrum", "Team Collaboration", "Project Management", "Time Management", "Leadership", "Critical Thinking"].map((skill) => (
                    <span key={skill} className="px-3 py-1 rounded-full text-xs font-medium bg-white/5 border border-white/10 text-slate-400">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
